import { api } from '@/store/helpers'
import { version } from '../../../../package.json'
import Application from 'App/Foundation/Application'
import {
	LOGOUT,
	SET_SESSION,
	SET_USER,
	SET_VERSION,
} from '@/store/mutation-types'
import util from '@/util'
/**
 * The module's dispatchable actions
 *
 * @author Alejandro Sanchez <asanchez@claruscare.com>
 */
export default {
	/**
	 * Check & get the current user's authentication status and data.
	 *
	 * @param {Function} options.commit
	 * @param {Function} options.dispatch
	 * @param {Object} options.state
	 * @return {Promise|Boolean}
	 */
	async check({ commit, dispatch, state }) {
		if (!state.session.access_token || !state.user.id) {
			return false
		}

		try {
			const response = await api()
				.users()
				.find(state.user.id)

			const user = response.get('data', {})

			await dispatch('getPartnerInfo', user)
			dispatch('setUser', user)

			return true
		} catch (e) {
			commit(LOGOUT)

			return Promise.reject(false)
		}
	},

	/**
	 * Attempt to authenticate a user with the given credentials.
	 *
	 * @param {Function} options.commit
	 * @param {Function} options.dispatch
	 * @param {Object} payload.username
	 * @param {Object} payload.password
	 * @return {Promise}
	 */
	async login({ commit, dispatch }, { username, password }) {
		const response = await api()
			.auth()
			.login(username, password)

		const {
			access_token,
			expires_in,
			refresh_token,
			token_type,
			...user
		} = response.get('data', {})

		const session = {
			access_token,
			expires_in,
			refresh_token,
			token_type,
		}

		commit(SET_SESSION, session)

		await dispatch('getPartnerInfo', user)
		dispatch('setUser', user)

		const app = Application.getInstance()

		app.emit('auth:success', app)

		return response
	},

	/**
	 * Logout the current user.
	 *
	 * @param {Function} options.commit
	 * @return {void}
	 */
	logout({ commit }) {
		commit(LOGOUT)
	},

	/**
	 * Set the authenticated user.
	 *
	 * @param {Function} options.commit
	 * @param {Function} options.dispatch
	 * @param {Object} model
	 * @return {void}
	 */
	async setUser({ commit, dispatch, rootState }, model) {
		const { ...user } = model

		const payload = rootState.partners?.pagination?.isSearching || false

		const partnerList = await dispatch('partners/getPartnerList', null, {
			root: true,
		})

		const newPartnerIds = partnerList
			.get('partners', [])
			.map(partner => partner.id)

		if (payload) {
			user.partner_ids = newPartnerIds
		} else {
			user.partner_ids = Array.from(
				new Set([...(user.partner_ids || []), ...newPartnerIds])
			)
		}

		user.messaging_profile_ids = []

		commit(SET_USER, user)
	},

	async getPartnerInfo({ dispatch, rootState }, user) {
		const { partners } = user

		const sortedPartners = util.sortPartners(partners || [])

		const defaultPartnerId = sortedPartners.length
			? sortedPartners[0].id
			: null

		const activePartnerId = rootState.partners?.active

		if (activePartnerId) {
			await dispatch('partners/find', activePartnerId, { root: true })
		}

		if (!activePartnerId && defaultPartnerId) {
			await dispatch('partners/setActive', defaultPartnerId, {
				root: true,
			})
		}

		if (!activePartnerId && !defaultPartnerId) {
			const page = rootState.partners.pagination.current_page || 1
			const keyword = rootState.partners.pagination.searchKeyword || ''

			const response = await api()
				.partners()
				.page(page)
				.search(keyword)
				.get()

			const sortedPartnersList = util.sortPartners(
				response.get('partners', [])
			)
			const firstPartnerId = sortedPartnersList[0]?.id || null
			await dispatch('partners/setActive', firstPartnerId, {
				root: true,
			})
		}
	},
	/**
	 * Set the current code base version.
	 *
	 * @param {Function} options.commit
	 * @param {Function} options.dispatch
	 * @return {void}
	 */
	async setVersion({ commit }) {
		commit(SET_VERSION, version)
	},
}
